// Verbatim copy used by the invite-members results UI. Centralized here so
// ticket-mandated strings live in exactly one place and cannot drift between
// the InviteOfficeMates screen and the onboarding AddMembers screen.
//
// The reason codes below MUST stay in lockstep with the backend constants in
// backend/utils/messages.js (INVITE_REASON_*). The backend returns
// errorRes: [{ email, reason }] from POST /users/v1/add-members, and this
// module turns each entry into a human-readable sentence.

import { buildAlreadyInvitedMessage } from './constants';

export const REASON_ALREADY_INVITED = 'already-invited';
export const REASON_INVALID_EMAIL = 'invalid-email';
export const REASON_DUPLICATE_IN_PAYLOAD = 'duplicate-in-payload';
export const REASON_INSERT_ERROR = 'insert-error';

// Shared suffix reused by the already-invited reason and the duplicate-mobile
// message so the wording lives in exactly one place.
export const ALREADY_INVITED_SUFFIX = 'has been invited before and can not be re-invited.';

// Message shown when a mobile number is rejected by the backend as a duplicate.
export const DUPLICATE_MOBILE_MESSAGE = `This mobile number ${ALREADY_INVITED_SUFFIX}`;

// Map of reason code -> human sentence fragment describing why the invite for
// that email failed. An unmapped/unknown reason degrades to a generic line so
// the UI never renders a raw code or a blank line.
const REASON_SENTENCES = {
  [REASON_ALREADY_INVITED]: ALREADY_INVITED_SUFFIX,
  [REASON_INVALID_EMAIL]: 'is not a valid email and could not be invited.',
  [REASON_DUPLICATE_IN_PAYLOAD]: 'was entered more than once and could not be invited.',
  [REASON_INSERT_ERROR]: 'could not be invited due to an unexpected error. Please try again.',
};

const GENERIC_REASON_SENTENCE = 'could not be invited.';

// Phone-only invites get a generated placeholder email (temp_...@exampleemail.com)
// that must never be shown to the admin.
const DUMMY_EMAIL_DOMAIN = '@exampleemail.com';

// How a user is named in result messages: "First Last" (or whichever part was
// given), else their real email. Returns '' when there is nothing to show.
const inviteEntryLabel = (entry) => {
  if (!entry) {
    return '';
  }
  const name = [entry.firstName, entry.lastName]
    .map((part) => (part ? part.toString().trim() : ''))
    .filter(Boolean)
    .join(' ');
  if (name) {
    return name;
  }
  const email = entry.email ? entry.email.toString() : '';
  if (email && !entry.isDummyEmail && !email.toLowerCase().endsWith(DUMMY_EMAIL_DOMAIN)) {
    return email;
  }
  return '';
};

const pluralizeUsers = (count) => `${count} ${count === 1 ? 'user' : 'users'}`;

// ["A"] -> "A"; ["A", "B"] -> "A and B"; ["A", "B", "C"] -> "A, B and C".
const joinWithAnd = (items) => {
  if (items.length <= 1) {
    return items.join('');
  }
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
};

// Builds the green success line, e.g. "3 users successfully added." /
// "1 user successfully added." Preserves singular/plural wording.
export const buildSuccessCountLine = (count) => {
  const verb = count === 1 ? 'has' : 'have';
  return `${pluralizeUsers(count)} ${verb} been successfully added.`;
};

// Builds the green success line, naming the added users, e.g.
// "Jane Doe has been successfully added." Users with no name or real email are
// counted instead of listed: "2 users have been successfully added." /
// "Jane Doe and 1 other user have been successfully added." A large batch
// falls back to the plain count past SUCCESS_NAME_LIMIT.
const SUCCESS_NAME_LIMIT = 5;
export const buildSuccessMessage = (successRes) => {
  if (!successRes || !successRes.length) {
    return '';
  }
  if (successRes.length > SUCCESS_NAME_LIMIT) {
    return buildSuccessCountLine(successRes.length);
  }
  const labels = successRes.map(inviteEntryLabel).filter(Boolean);
  const unnamedCount = successRes.length - labels.length;
  if (labels.length === 0) {
    return buildSuccessCountLine(unnamedCount);
  }
  if (unnamedCount > 0) {
    labels.push(`${unnamedCount} other ${unnamedCount === 1 ? 'user' : 'users'}`);
  }
  const verb = successRes.length === 1 ? 'has' : 'have';
  return `${joinWithAnd(labels)} ${verb} been successfully added.`;
};

// Returns the human sentence for a single reason code, falling back to a
// generic sentence for any unknown/unmapped/empty code.
export const reasonToSentence = (reason) => {
  if (reason && REASON_SENTENCES[reason]) {
    return REASON_SENTENCES[reason];
  }
  return GENERIC_REASON_SENTENCE;
};

// Turns the backend errorRes array ([{ email, reason }]) into a single
// displayable failure message with one sentence per named failure. Failures
// with nothing presentable (e.g. phone-only users) are grouped per reason into
// one "N users ..." sentence instead of repeating the same line.
// Returns '' when there are no failures.
export const buildFailureMessage = (errorRes) => {
  if (!errorRes || !errorRes.length) {
    return '';
  }
  // "already-invited" failures are consolidated into ONE sentence via
  // buildAlreadyInvitedMessage (the Users-List wording, incl. the "this account
  // or another account" phrasing) instead of repeating a sentence per email.
  // Every other reason is still rendered as one short sentence per email.
  const alreadyInvited = [];
  const otherLines = [];
  const unnamedByReason = {};
  errorRes.forEach((entry) => {
    const reason = entry && entry.reason;
    const label = inviteEntryLabel(entry);
    if (reason === REASON_ALREADY_INVITED) {
      alreadyInvited.push(label || '');
    } else if (label) {
      otherLines.push(`${label} ${reasonToSentence(reason)}`);
    } else {
      const key = reason || '';
      unnamedByReason[key] = (unnamedByReason[key] || 0) + 1;
    }
  });
  const parts = [];
  if (alreadyInvited.length) {
    const named = alreadyInvited.filter(Boolean);
    const unnamedCount = alreadyInvited.length - named.length;
    if (unnamedCount === 0) {
      parts.push(buildAlreadyInvitedMessage(named));
    } else {
      const counted = named.length ? `${unnamedCount} other ${unnamedCount === 1 ? 'user' : 'users'}` : pluralizeUsers(unnamedCount);
      const verb = alreadyInvited.length > 1 ? 'have' : 'has';
      parts.push(`${joinWithAnd([...named, counted])} ${verb} already been invited to this account or in another account, and cannot be re-invited.`);
    }
  }
  if (otherLines.length) {
    parts.push(otherLines.join(' '));
  }
  Object.keys(unnamedByReason).forEach((reason) => {
    const count = unnamedByReason[reason];
    let sentence = reasonToSentence(reason);
    if (count > 1) {
      sentence = sentence.replace(/^is /, 'are ').replace(/^was /, 'were ').replace(/^has /, 'have ');
    }
    parts.push(`${pluralizeUsers(count)} ${sentence}`);
  });
  return parts.join(' ');
};
